import { useEffect, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import type { TrendingDestination } from 'home/components/TrendingDestinations/constants'
import { ONLY_IN_AUSTRALIA_CAMPAIGN_PATH } from 'home/constants/onlyInAustraliaHeroCampaign'
import {
  getTrendingDestinationsCacheKey,
  getTrendingDestinationsForFilter,
  prioritiseAustraliaDestinationForAuRegion,
} from 'home/components/TrendingDestinations/constants'
import { fetchTrendingDestinations } from 'actions/DestinationAlertsActions'
import { buildHotelUrl } from 'home/components/TrendingDestinations/utils'
import { EmptyArray } from 'lib/array/arrayUtils'
import config from 'constants/config'
import { useLocalizedPlaces } from 'hooks/usePlace'

// START constants for Only In Australia campaign. TODO: clean up when campaign is over
const AUSTRALIA_PLACE_ID = 'le_d3d9446802a44259755d38e6d163e820'
const ONLY_IN_AUSTRALIA_CAMPAIGN_END_DATE = new Date('2026-05-08T23:59:59+10:00')
// END constants for Only In Australia campaign

export function useHotelTrendingDestinations(
  selectedFilter: App.DestinationSelectedFilters,
  currentRegionCode: string,
  leUserId?: string,
  domainUserId?: string,
  disablePersonalisation?: boolean,
): [Array<TrendingDestination>, boolean] {
  const dispatch = useAppDispatch()

  // Create the same cache key used by the action
  const userKey = getTrendingDestinationsCacheKey(leUserId, domainUserId, disablePersonalisation)

  useEffect(() => {
    dispatch(fetchTrendingDestinations(leUserId, domainUserId, disablePersonalisation))
  }, [dispatch, leUserId, domainUserId, disablePersonalisation])

  const trendingDestinations = useAppSelector(
    state => state.destination?.trendingDestinationsByUserKey?.[userKey],
  ) ?? EmptyArray

  const fetching = useAppSelector(
    state => state.destination?.fetchingTrendingDestinationsByUserKey?.[userKey] ?? false,
  )

  // URL construction below still uses the English `primaryText` to keep link paths locale-stable.
  const placeIds = useMemo(
    () => trendingDestinations.filter(d => d.imageId).map(d => d.placeId),
    [trendingDestinations],
  )
  const localizedPlaces = useLocalizedPlaces(placeIds)

  const displayTrending: Array<TrendingDestination> = useMemo(() => {
    if (fetching) return []

    const shouldUseCampaignUrl = (placeId: string) => {
      const nowDate = new Date()
      return config.BRAND === 'luxuryescapes' && placeId === AUSTRALIA_PLACE_ID && nowDate < ONLY_IN_AUSTRALIA_CAMPAIGN_END_DATE
    }
    const mappedTrendingDestinations = trendingDestinations
      .filter(destination => destination.imageId)
      .map(destination => ({
        name: localizedPlaces[destination.placeId]?.name ?? destination.primaryText,
        imageId: destination.imageId,
        url: shouldUseCampaignUrl(destination.placeId) ? ONLY_IN_AUSTRALIA_CAMPAIGN_PATH : buildHotelUrl(destination.primaryText, destination.placeId),
        destinationId: destination.placeId,
      } as TrendingDestination))

    return prioritiseAustraliaDestinationForAuRegion(mappedTrendingDestinations, currentRegionCode)
  }, [trendingDestinations, fetching, localizedPlaces, currentRegionCode])

  if (selectedFilter !== 'hotel') {
    const hardCodedTrendingDestinations = getTrendingDestinationsForFilter(selectedFilter, currentRegionCode) ?? []
    return [hardCodedTrendingDestinations, false]
  }

  return [displayTrending, fetching]
}
